import { Camera } from "lucide-react";
import { useMemo, useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import type { SitePhoto } from "@/hooks/use-site-photos";
import { useSitePhotos } from "@/hooks/use-site-photos";

const CATEGORY_OPTIONS = [
  { value: "all", label: "All categories" },
  { value: "progress", label: "Progress" },
  { value: "safety", label: "Safety" },
  { value: "quality", label: "Quality" },
  { value: "issue", label: "Issue" },
  { value: "before", label: "Before" },
  { value: "after", label: "After" },
  { value: "other", label: "Other" },
] as const;

function photoCaption(photo: SitePhoto): string {
  const parts: string[] = [];
  if (photo.caption) parts.push(photo.caption);
  if (photo.location) parts.push(photo.location);
  if (photo.takenByName) parts.push(`— ${photo.takenByName}`);
  return parts.join(" · ") || "Site photo";
}

export function ProjectSitePhotosTab({ projectId }: { projectId: string }) {
  const [category, setCategory] = useState<string>("all");

  const filters = useMemo(() => {
    return {
      projectId,
      ...(category !== "all" ? { category } : {}),
    };
  }, [projectId, category]);

  const { data, isLoading, isError, error, refetch } = useSitePhotos(filters);

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {(["a", "b", "c", "d", "e", "f", "g", "h"] as const).map((k) => (
          <Skeleton key={k} className="aspect-square rounded-lg" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : "Failed to load photos"}
        onRetry={() => void refetch()}
      />
    );
  }

  const photos = data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <p className="text-sm text-muted-foreground">
          {photos.length === 0
            ? "No photos yet."
            : `${photos.length} photo${photos.length === 1 ? "" : "s"}`}
        </p>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="min-h-[44px] w-[200px]">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            {CATEGORY_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {photos.length === 0 ? (
        <Card>
          <CardContent className="py-16 flex flex-col items-center gap-3 text-center text-muted-foreground">
            <Camera className="h-10 w-10 opacity-40" />
            <p className="text-sm max-w-sm">
              Photos attached to this project or daily logs appear here. Upload from a daily log’s
              Photos tab.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {photos.map((photo) => (
            <a
              key={photo.id}
              href={photo.fullUrl}
              target="_blank"
              rel="noreferrer"
              className="group relative aspect-square rounded-lg overflow-hidden border bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <img
                src={photo.thumbnailUrl}
                alt={photoCaption(photo)}
                className="h-full w-full object-cover transition-transform group-hover:scale-[1.02]"
                loading="lazy"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 pt-8">
                <p className="text-[11px] text-white line-clamp-2">{photoCaption(photo)}</p>
                <Badge variant="secondary" className="mt-1 text-[10px] capitalize">
                  {photo.category}
                </Badge>
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
